import { TOOLS } from '../core/tools';
import { useEditorStore } from '../state/editorStore';

/**
 * Tool options bar.
 *
 * Tool settings are view state, not document state: they update live and never
 * touch history. A brush-size drag that pushed a history entry per frame would
 * bury undo before you reached the value you wanted.
 */

const PAINT_TOOLS = new Set(['brush', 'pencil', 'eraser']);
const SHAPE_TOOLS = new Set(['line', 'rect', 'ellipse', 'arrow']);
const CLOSED_SHAPE_TOOLS = new Set(['rect', 'ellipse']);

const BAR =
  'flex h-8 shrink-0 items-center gap-3 overflow-x-auto border-b border-line bg-surface px-3 [&>*]:shrink-0';

function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  suffix = '',
  onCommit,
  testId,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onCommit: (value: number) => void;
  testId: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-muted">
      <span className="w-14 shrink-0">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        data-testid={testId}
        onChange={(event) => onCommit(Number(event.target.value))}
        className="w-24 accent-(--color-accent)"
      />
      <span className="w-9 text-right tabular-nums text-ink">
        {value}
        {suffix}
      </span>
    </label>
  );
}

function ColourPicker() {
  const color = useEditorStore((s) => s.color);
  const setColor = useEditorStore((s) => s.setColor);
  const recentColors = useEditorStore((s) => s.recentColors);
  return (
    <div className="ml-auto flex items-center gap-1.5">
      <input
        type="color"
        aria-label="Foreground colour"
        value={color}
        data-testid="option-color"
        onChange={(event) => setColor(event.target.value)}
        className="size-6 cursor-pointer rounded border border-line bg-surface-2"
      />
      <div className="flex items-center gap-0.5" data-testid="recent-colors">
        {recentColors.slice(0, 8).map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-label={`Use ${candidate}`}
            title={candidate}
            onClick={() => setColor(candidate)}
            className="size-4 rounded-sm border border-line"
            style={{ background: candidate }}
          />
        ))}
      </div>
    </div>
  );
}

function ShapeOptionsBar({ tool, closed }: { tool: string; closed: boolean }) {
  const shape = useEditorStore((s) => s.shape);
  const setShape = useEditorStore((s) => s.setShape);
  return (
    <div className={BAR} data-testid="tool-options">
      <span className="text-[11px] text-ink">{tool}</span>
      <Slider
        label="Width"
        value={shape.width}
        min={1}
        max={64}
        onCommit={(value) => setShape({ width: value })}
        testId="option-stroke-width"
      />
      {closed ? (
        <label className="flex items-center gap-1.5 text-[11px] text-muted">
          <input
            type="checkbox"
            checked={shape.filled}
            data-testid="option-filled"
            onChange={(event) => setShape({ filled: event.target.checked })}
            className="accent-(--color-accent)"
          />
          Filled
        </label>
      ) : null}
      <ColourPicker />
    </div>
  );
}

function FillOptionsBar() {
  const fillTolerance = useEditorStore((s) => s.fillTolerance);
  const setFillTolerance = useEditorStore((s) => s.setFillTolerance);
  return (
    <div className={BAR} data-testid="tool-options">
      <span className="text-[11px] text-ink">Fill</span>
      <Slider
        label="Tolerance"
        value={fillTolerance}
        min={0}
        max={100}
        suffix="%"
        onCommit={setFillTolerance}
        testId="option-tolerance"
      />
      <span className="text-[11px] text-muted">Click a region to flood it</span>
      <ColourPicker />
    </div>
  );
}

function TextOptionsBar() {
  const text = useEditorStore((s) => s.text);
  const setText = useEditorStore((s) => s.setText);
  return (
    <div className={BAR} data-testid="tool-options">
      <span className="text-[11px] text-ink">Text</span>
      <Slider
        label="Size"
        value={text.fontSize}
        min={6}
        max={400}
        onCommit={(value) => setText({ fontSize: value })}
        testId="option-font-size"
      />
      <label className="flex items-center gap-1.5 text-[11px] text-muted">
        <input
          type="checkbox"
          checked={text.bold}
          data-testid="option-bold"
          onChange={(event) => setText({ bold: event.target.checked })}
          className="accent-(--color-accent)"
        />
        Bold
      </label>
      <label className="flex items-center gap-1.5 text-[11px] text-muted">
        <input
          type="checkbox"
          checked={text.italic}
          data-testid="option-italic"
          onChange={(event) => setText({ italic: event.target.checked })}
          className="accent-(--color-accent)"
        />
        Italic
      </label>
      <span className="text-[11px] text-muted">Click the canvas to write</span>
      <ColourPicker />
    </div>
  );
}

export function ToolOptions() {
  const activeTool = useEditorStore((s) => s.activeTool);
  const paint = useEditorStore((s) => s.paint);
  const setPaint = useEditorStore((s) => s.setPaint);
  const setTool = useEditorStore((s) => s.setTool);

  const tool = TOOLS.find((candidate) => candidate.id === activeTool);
  const isPainting = PAINT_TOOLS.has(activeTool);
  const isShape = SHAPE_TOOLS.has(activeTool);

  if (isShape) {
    return (
      <ShapeOptionsBar tool={tool?.label ?? 'Shape'} closed={CLOSED_SHAPE_TOOLS.has(activeTool)} />
    );
  }

  if (activeTool === 'fill') return <FillOptionsBar />;

  if (activeTool === 'text') return <TextOptionsBar />;

  if (activeTool === 'eyedropper') {
    return (
      <div className={BAR} data-testid="tool-options">
        <span className="text-[11px] text-ink">Eyedropper</span>
        <span className="text-[11px] text-muted">Click the canvas to pick a colour</span>
        {/* The picked colour lands here, so the swatch belongs in this bar. */}
        <ColourPicker />
      </div>
    );
  }

  if (!isPainting) {
    return (
      <div
        className="flex h-8 shrink-0 items-center gap-3 border-b border-line bg-surface px-3 text-[11px] text-muted"
        data-testid="tool-options"
      >
        <span className="text-ink">{tool?.label ?? 'Tool'}</span>
        {tool?.plannedFor ? (
          <span data-testid="tool-options-note">Available in Iteration {tool.plannedFor}</span>
        ) : (
          <span data-testid="tool-options-note">{tool?.hint}</span>
        )}
        {activeTool === 'crop' ? (
          <button
            type="button"
            className="ml-auto rounded border border-line px-2 py-0.5 hover:border-accent"
            onClick={() => setTool('crop')}
          >
            Drag on the canvas to select a region
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={BAR} data-testid="tool-options">
      <Slider
        label="Size"
        value={paint.size}
        min={1}
        max={200}
        onCommit={(value) => setPaint({ size: value })}
        testId="option-size"
      />
      {activeTool !== 'pencil' ? (
        <Slider
          label="Hardness"
          value={Math.round(paint.hardness * 100)}
          min={0}
          max={100}
          suffix="%"
          onCommit={(value) => setPaint({ hardness: value / 100 })}
          testId="option-hardness"
        />
      ) : null}
      <Slider
        label="Opacity"
        value={Math.round(paint.opacity * 100)}
        min={1}
        max={100}
        suffix="%"
        onCommit={(value) => setPaint({ opacity: value / 100 })}
        testId="option-opacity"
      />
      {activeTool === 'brush' ? (
        <Slider
          label="Steady"
          value={Math.round(paint.smoothing * 100)}
          min={0}
          max={95}
          suffix="%"
          onCommit={(value) => setPaint({ smoothing: value / 100 })}
          testId="option-smoothing"
        />
      ) : null}

      {activeTool !== 'eraser' ? <ColourPicker /> : null}
    </div>
  );
}
